import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import {
  useGetCourseBySlugQuery,
  useGetCourseProgressQuery,
  useStartLessonExerciseMutation,
  useSaveExerciseAnswerMutation,
  useSubmitLessonExerciseMutation,
  useGetAttemptByIdQuery,
} from "@/features/api/courseApi";
import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ChevronDown } from "lucide-react";

/* ================= EXERCISES ================= */
import MultipleChoiceExercise from "@/assets/AllCourses/Exercises/MultipleChoice";
import FillInTheBlanksExercise from "@/assets/AllCourses/Exercises/FillInTheBlanks";
import PictureFillInTheBlanksExercise from "@/assets/AllCourses/Exercises/PictureFillInTheBlanks";
import MatchingExercise from "@/assets/AllCourses/Exercises/Matching";
import PronunciationExercise from "@/assets/AllCourses/Exercises/Pronunciation";
import ListeningExercise from "@/assets/AllCourses/Exercises/Listening";
import DragAndDropExercise from "@/assets/AllCourses/Exercises/DragAndDrop";
import CategorizingWordsExercise from "@/assets/AllCourses/Exercises/CategorizingWords";
import CircleTheCorrectWordExercise from "@/assets/AllCourses/Exercises/CircleTheCorrectWord";
import CompleteTheConversationExercise from "@/assets/AllCourses/Exercises/CompleteTheConversation";
import CompleteTheSentencesExercise from "@/assets/AllCourses/Exercises/CompleteTheSentences";
import CrosswordExercise from "@/assets/AllCourses/Exercises/Crossword";
import MissingAlphabetsExercise from "@/assets/AllCourses/Exercises/MissingAlphabets";
import SentenceJumblesExercise from "@/assets/AllCourses/Exercises/SentenceJumbles";
import StressingSyllableWordsExercise from "@/assets/AllCourses/Exercises/StressingSyllableWords";
import VocabularyBuildingExercise from "@/assets/AllCourses/Exercises/VocabularyBuilding";
import WordFormationExercise from "@/assets/AllCourses/Exercises/WordFormation";
import WordJumblesExercise from "@/assets/AllCourses/Exercises/WordJumbles";

/* ================= CONSTANTS ================= */
const SCROLL_OFFSET = 140;
const STORAGE_KEY_PREFIX = "lesson_";

/* ================= EXERCISE COMPONENTS MAP ================= */
const exerciseComponents = {
  "multiple-choice": MultipleChoiceExercise,
  "fill-in-the-blanks": FillInTheBlanksExercise,
  "picture-fill-in-the-blanks": PictureFillInTheBlanksExercise,
  matching: MatchingExercise,
  pronunciation: PronunciationExercise,
  listening: ListeningExercise,
  "drag-and-drop": DragAndDropExercise,
  "categorizing-words": CategorizingWordsExercise,
  "circle-the-correct-word": CircleTheCorrectWordExercise,
  "complete-the-conversation": CompleteTheConversationExercise,
  "complete-the-sentences": CompleteTheSentencesExercise,
  crossword: CrosswordExercise,
  "missing-alphabets": MissingAlphabetsExercise,
  "sentence-jumbles": SentenceJumblesExercise,
  "stressing-syllable-words": StressingSyllableWordsExercise,
  "vocabulary-building": VocabularyBuildingExercise,
  "word-formation": WordFormationExercise,
  "word-jumbles": WordJumblesExercise,
  "exercise-section": () => null,
};

export default function CourseLearningPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ================= REFS ================= */
  const startTimeRef = useRef(Date.now());
  const lastSavedAnswersRef = useRef({});
  const initRef = useRef(false);
  const saveQueueRef = useRef({});
  const savingRef = useRef(false);

  /* ================= STATE ================= */
  const [openAccordion, setOpenAccordion] = useState(true);
  const [answers, setAnswers] = useState({});
  const [attemptId, setAttemptId] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(null);
  const [retryMode, setRetryMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);

  /* ================= API HOOKS ================= */
  const { data, isLoading } = useGetCourseBySlugQuery(slug, { skip: !slug });
  const course = data?.course;

  const { data: progressData, isLoading: progressLoading } =
    useGetCourseProgressQuery(course?._id, { skip: !course?._id });

  const [startAttempt] = useStartLessonExerciseMutation();
  const [saveAnswer] = useSaveExerciseAnswerMutation();
  const [submitAttempt] = useSubmitLessonExerciseMutation();

  const { data: attemptData } = useGetAttemptByIdQuery(attemptId, {
    skip: !attemptId,
  });

  /* ================= DERIVED VALUES ================= */
  const lessonIdFromUrl = searchParams.get("lesson");
  const PASS_PERCENT = Number(course?.passPercentage || 30);

  const sortedLessons = useMemo(() => {
    return [...(course?.lessons || [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
  }, [course]);

  const lesson = useMemo(() => {
    if (!course || !sortedLessons.length) return null;

    const fromUrl = sortedLessons.find(
      (l) => l._id?.toString() === lessonIdFromUrl
    );
    if (fromUrl) return fromUrl;

    const incomplete = sortedLessons.find((l) =>
      progressData?.lessonsProgress?.some(
        (lp) =>
          lp.lessonId?.toString() === l._id?.toString() && !lp.completed
      )
    );
    return incomplete || sortedLessons[0];
  }, [sortedLessons, lessonIdFromUrl, progressData, course]);

  const allExercises = useMemo(() => {
    const result = [];
    sortedLessons.forEach((lessonItem) => {
      (lessonItem.exercises || []).forEach((ex, index) => {
        if (ex.type !== "exercise-section") {
          result.push({
            ...ex,
            lessonId: lessonItem._id,
            lessonTitle: lessonItem.title,
            order: index,
          });
        }
      });
    });
    return result;
  }, [sortedLessons]);

  const totalQuestions = useMemo(() => {
    return allExercises.reduce((total, ex) => {
      const questions =
        ex.multipleChoiceQuestions ||
        ex.fillInTheBlanksQuestions ||
        ex.pictureFillInTheBlanksQuestions ||
        ex.matchingPairs ||
        ex.listeningQuestions ||
        ex.pronunciationQuestions ||
        ex.dragAndDropQuestions ||
        ex.wordChoiceQuestions ||
        ex.conversationQuestions ||
        ex.sentenceCompletionQuestions ||
        ex.missingAlphabetsQuestions ||
        ex.sentenceJumblesQuestions ||
        ex.stressingSyllableQuestions ||
        ex.vocabularyQuestions ||
        ex.wordFormationQuestions ||
        ex.wordJumblesQuestions ||
        [];
      return total + (Array.isArray(questions) ? questions.length : 1);
    }, 0);
  }, [allExercises]);

  const answeredCount = useMemo(() => {
    let count = 0;
    allExercises.forEach((ex) => {
      const ans = answers[ex._id]?.answer;
      if (!ans) return;
      if (Array.isArray(ans)) {
        count += ans.filter((v) => v !== "" && v != null).length;
      } else if (typeof ans === "object") {
        count += Object.values(ans).filter((v) => v !== "" && v != null).length;
      } else {
        count += 1;
      }
    });
    return count;
  }, [answers, allExercises]);

  const progressValue = totalQuestions === 0 ? 0 : Math.round((answeredCount / totalQuestions) * 100);

  const currentAnswered = useMemo(() => {
    const ex = allExercises[currentExerciseIndex];
    if (!ex) return false;
    const ans = answers[ex._id]?.answer;
    if (!ans) return false;
    if (Array.isArray(ans)) return ans.some((v) => v !== "" && v != null);
    if (typeof ans === "object") {
      return Object.values(ans).some((v) => v !== "" && v != null);
    }
    return true;
  }, [allExercises, currentExerciseIndex, answers]);

  const grouped = useMemo(() => {
    const groups = {};
    allExercises.forEach((ex, index) => {
      if (!groups[ex.lessonTitle]) groups[ex.lessonTitle] = [];
      groups[ex.lessonTitle].push({ ...ex, index });
    });
    return Object.entries(groups).map(([title, items]) => ({ title, items }));
  }, [allExercises]);

  /* ================= LOAD ATTEMPT DATA ================= */
  useEffect(() => {
    if (!attemptData?.answers) return;

    const mapped = {};
    attemptData.answers.forEach((a) => {
      mapped[a.exerciseId] = { answer: a.answer };
    });

    setAnswers(mapped);
    lastSavedAnswersRef.current = mapped;
  }, [attemptData]);

  /* ================= START ATTEMPT ================= */
  useEffect(() => {
    if (!lesson?._id || initRef.current) return;

    initRef.current = true;
    const init = async () => {
      try {
        const res = await startAttempt({ lessonId: lesson._id }).unwrap();
        setAttemptId(res?.attemptId);
        startTimeRef.current = Date.now();
      } catch (err) {
        console.error("Failed to start attempt", err);
      }
    };
    init();
  }, [lesson?._id, startAttempt]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!attemptId || showResult) return;

    const pending = Object.entries(answers).filter(([id, val]) => {
      if (!val?.answer) return false;
      const prev = lastSavedAnswersRef.current[id];
      return JSON.stringify(prev) !== JSON.stringify(val);
    });

    if (!pending.length || savingRef.current) return;

    savingRef.current = true;

    Promise.all(
      pending.map(([id, val]) =>
        saveAnswer({
          attemptId,
          exerciseId: id,
          answer: val.answer,
        })
      )
    )
      .then(() => {
        lastSavedAnswersRef.current = { ...answers };
      })
      .finally(() => {
        savingRef.current = false;
      });
  }, [answers, attemptId, showResult, saveAnswer]);

  /* ================= LOCAL STORAGE ================= */
  const STORAGE_KEY = `${STORAGE_KEY_PREFIX}${slug}`;

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setAnswers(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse saved answers");
      }
    }
  }, [STORAGE_KEY]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  }, [answers, STORAGE_KEY]);

  /* ================= HANDLERS ================= */
  const getTitle = (ex, i) => ex?.title?.trim() || `Exercise ${i + 1}`;

  const handleComplete = useCallback((id, engineData) => {
    const answer = engineData?.answer;
    if (
      answer === undefined ||
      answer === null ||
      (typeof answer === "string" && answer.trim() === "") ||
      (typeof answer === "object" && Object.keys(answer).length === 0)
    ) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      [id]: {
        answer: typeof answer === "object" ? answer : String(answer),
        correct: engineData?.correct ?? null,
      },
    }));
  }, []);

  const continueLesson = () => {
    const nextIndex = currentExerciseIndex + 1;
    if (nextIndex >= allExercises.length) return;
    setCurrentExerciseIndex(nextIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBackExercise = () => {
    if (currentExerciseIndex === 0) return;
    setCurrentExerciseIndex((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const retryLesson = async () => {
    try {
      setAnswers({});
      setShowResult(false);
      setRetryMode(false);
      setScore(null);
      setCurrentExerciseIndex(0);
      lastSavedAnswersRef.current = {};

      const res = await startAttempt({ lessonId: lesson._id }).unwrap();
      setAttemptId(res?.attemptId);
      startTimeRef.current = Date.now();
    } catch {
      toast.error("Retry failed");
    }
  };

  const submit = async () => {
    if (isSubmitting) return;

    const isAllAnswered = allExercises.every((ex) => {
      const ans = answers[ex._id]?.answer;
      if (!ans) return false;
      if (Array.isArray(ans)) return ans.every((v) => v !== "" && v != null);
      if (typeof ans === "object") {
        return Object.values(ans).every((v) => v !== "" && v != null);
      }
      return ans !== "";
    });

    if (!isAllAnswered) {
      return toast.error("Please complete all questions before submitting.");
    }

    try {
      setIsSubmitting(true);
      const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);

      const payload = allExercises
        .map((ex) => {
          const a = answers[ex._id];
          if (!a) return null;
          return { exerciseId: ex._id, answer: a.answer };
        })
        .filter(Boolean);

      const res = await submitAttempt({
        attemptId,
        answers: payload,
        timeSpent,
      }).unwrap();

      setScore(res.scorePercent);
      setShowResult(true);
      setRetryMode(res.scorePercent < PASS_PERCENT);

      // Update correct answers from backend
      setAnswers((prev) => {
        const updated = { ...prev };
        res.answers?.forEach((a) => {
          if (updated[a.exerciseId]) {
            updated[a.exerciseId].correct = a.correct;
          }
        });
        return updated;
      });
    } catch {
      toast.error("Failed to submit exercise");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================= LOADING STATE ================= */
  if (isLoading || progressLoading || !lesson) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!course) {
    return <div className="p-8 text-red-600">Course not found</div>;
  }

  const currentExercise = allExercises[currentExerciseIndex];
  const Comp = currentExercise ? exerciseComponents[currentExercise.type] : null;

  return (
    <div className="flex gap-6 p-6">
      {/* Table of Contents */}
      <aside className="w-80 bg-white p-6 rounded-xl shadow sticky top-6 h-fit">
        <button
          onClick={() => setOpenAccordion((p) => !p)}
          className="flex justify-between w-full font-bold text-xl mb-4"
        >
          {course.title}
          <ChevronDown className={`transition-transform ${openAccordion ? "rotate-180" : ""}`} />
        </button>

        {openAccordion &&
          grouped.map((group, gi) => (
            <div key={gi} className="mb-4">
              <h4 className="text-sm font-bold mb-2">{group.title}</h4>
              {group.items.map((ex) => {
                const active = currentExerciseIndex === ex.index;
                return (
                  <button
                    key={ex.index}
                    onClick={() => {
                      setCurrentExerciseIndex(ex.index);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`w-full text-left px-4 py-2 rounded mb-1 transition-colors ${
                      active
                        ? "bg-blue-100 text-blue-700 border-l-4 border-blue-600"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    {getTitle(ex, ex.index)}
                  </button>
                );
              })}
            </div>
          ))}
      </aside>

      {/* Main Content */}
      <main className="flex-1">
        {currentExercise && Comp && (
          <section className="mb-14 relative">
            <div className="absolute top-0 right-0 bg-white/80 backdrop-blur">
              <img
                src="https://fetc.in/image/logo/Syzygy.png"
                alt="Syzygy"
                className="h-[11.5rem] object-contain opacity-90"
              />
            </div>

            <h3 className="text-xl font-bold mb-6">
              {getTitle(currentExercise, currentExerciseIndex)}
            </h3>

            {currentExercise.details && (
              <div
                className="mb-6"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(currentExercise.details),
                }}
              />
            )}

            <div className="p-6 rounded border bg-white">
              <Comp
                exercise={currentExercise}
                showResult={showResult}
                userAnswer={answers[currentExercise._id]?.answer}
                isCorrect={answers[currentExercise._id]?.correct}
                onComplete={(engineData) =>
                  handleComplete(currentExercise._id, engineData)
                }
              />
            </div>
          </section>
        )}

        <CardFooter className="mt-8 border-t pt-6">
          <div className="w-full flex items-center justify-between gap-4">
            {/* Back Button */}
            <Button
              onClick={goBackExercise}
              disabled={currentExerciseIndex === 0}
              variant="outline"
              className="min-w-[110px]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            {/* Right Side Actions */}
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                {!showResult ? (
                  currentExerciseIndex < allExercises.length - 1 ? (
                    <Button
                      onClick={continueLesson}
                      disabled={!currentAnswered}
                      className="min-w-[110px]"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={submit}
                      disabled={isSubmitting || answeredCount < totalQuestions}
                      className="min-w-[110px]"
                    >
                      Submit
                    </Button>
                  )
                ) : (
                  <>
                    {retryMode && (
                      <Button
                        variant="destructive"
                        onClick={retryLesson}
                        className="min-w-[110px]"
                      >
                        Retry
                      </Button>
                    )}

                    {currentExerciseIndex === allExercises.length - 1 && (
                      <Button
                        onClick={() => navigate(`/dashboard/courses/${slug}/result`)}
                        disabled={!showResult || Number(score) < PASS_PERCENT}
                        className="min-w-[110px]"
                      >
                        Continue
                      </Button>
                    )}
                  </>
                )}
              </div>

              {showResult && Number(score) < PASS_PERCENT && (
                <p className="text-sm text-red-500 text-right max-w-[220px]">
                  You must score at least {PASS_PERCENT}% to continue
                </p>
              )}
            </div>
          </div>
        </CardFooter>
      </main>
    </div>
  );
}